import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Checkbox, Modal, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { DetectionAnnotationEditor } from '../../../components/history/HistoryDetail/DetectionAnnotationEditor'
import type { HistoryPrediction } from '../../../types/history'
import type {
    ValidationDatasetSample,
    ValidationDatasetVersion,
} from '../../../types/modelValidation'
import {
    predictionsToValidationAnnotations,
    validationAnnotationsToPredictions,
} from './modelValidationUtils'
import type { HistoryLabelOption } from '../../../types/history'

interface ValidationDatasetAnnotationModalProps {
    labelOptions: HistoryLabelOption[]
    loading: boolean
    onClose: () => void
    onSave: (payload: {
        annotations: ReturnType<typeof predictionsToValidationAnnotations>
        reviewed: boolean
    }) => void
    open: boolean
    sample: ValidationDatasetSample | null
    version: ValidationDatasetVersion | null
}

const ValidationDatasetAnnotationModal: React.FC<
    ValidationDatasetAnnotationModalProps
> = ({ labelOptions, loading, onClose, onSave, open, sample, version }) => {
    const { t } = useTranslation(['admin'])
    const seedPredictions = useMemo(
        () =>
            sample
                ? validationAnnotationsToPredictions(
                      sample.annotations,
                      labelOptions
                  )
                : [],
        [labelOptions, sample]
    )
    const [predictions, setPredictions] = useState<HistoryPrediction[]>([])
    const [zeroTargetReviewed, setZeroTargetReviewed] = useState(false)

    useEffect(() => {
        setPredictions(seedPredictions)
        setZeroTargetReviewed(Boolean(sample?.annotation_reviewed))
    }, [sample?.annotation_reviewed, sample?.id, seedPredictions])

    const annotations = predictionsToValidationAnnotations(predictions)
    const zeroTargetNeedsReview = annotations.length === 0
    const canSave =
        Boolean(sample && version?.status === 'draft') &&
        (!zeroTargetNeedsReview || zeroTargetReviewed)

    return (
        <Modal
            cancelText={t('admin:modelValidation.actions.cancel')}
            destroyOnClose
            maskClosable={false}
            okButtonProps={{ disabled: !canSave }}
            okText={t('admin:modelValidation.actions.saveAnnotations')}
            onCancel={onClose}
            onOk={() =>
                onSave({
                    annotations,
                    reviewed: annotations.length > 0 || zeroTargetReviewed,
                })
            }
            open={open}
            confirmLoading={loading}
            title={t('admin:modelValidation.datasets.annotationTitle', {
                filename: sample?.filename || '',
            })}
            width="min(1180px, 96vw)"
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {labelOptions.length === 0 ? (
                    <Alert
                        message={t(
                            'admin:modelValidation.datasets.noCategoryOptions'
                        )}
                        type="warning"
                        showIcon
                    />
                ) : null}
                {sample ? (
                    <DetectionAnnotationEditor
                        dirty={
                            JSON.stringify(predictions) !==
                            JSON.stringify(seedPredictions)
                        }
                        editable={version?.status === 'draft'}
                        filename={sample.filename}
                        imageSrc={sample.image_url}
                        labelOptions={labelOptions}
                        onChange={setPredictions}
                        onReset={() => setPredictions(seedPredictions)}
                        showAnnotationLabels={false}
                        seedKey={`${sample.id}:${version?.lock_version || 0}`}
                        seedPredictions={seedPredictions}
                    />
                ) : null}
                {zeroTargetNeedsReview ? (
                    <Checkbox
                        checked={zeroTargetReviewed}
                        disabled={version?.status !== 'draft'}
                        onChange={(event) =>
                            setZeroTargetReviewed(event.target.checked)
                        }
                    >
                        {t('admin:modelValidation.datasets.zeroTargetReviewed')}
                    </Checkbox>
                ) : (
                    <Typography.Text type="secondary">
                        {t(
                            'admin:modelValidation.datasets.annotatedObjectCount',
                            {
                                count: annotations.length,
                            }
                        )}
                    </Typography.Text>
                )}
            </Space>
        </Modal>
    )
}

export default ValidationDatasetAnnotationModal
